import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  FactCheck as GradeIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { analyzeEvidence } from '../../api/evidenceApi';
import { loadRemoteProgress, saveRemoteProgress } from '../../api/progressApi';

const TRACKER_ID = 'dq-math-04-outliers-practice-bank';

const LENSES = [
  ['stat_rule', 'Detection rule'],
  ['evidence', 'Evidence'],
  ['business_check', 'Business check'],
  ['outlier_bucket', 'Root cause'],
  ['treatment', 'Treatment'],
  ['caution', 'Caution'],
  ['verify', 'Verify'],
  ['learner_sentence', 'Learner sentence'],
];

const WORKING_PROMPTS = {
  O1: 'Confirm the lane: IQR, because this is a single numeric distribution question. Show sorted amounts, Q1, Q3, IQR, lower fence, upper fence, and the row the fence flags.',
  O2: 'Confirm the lane: IQR, then segmented IQR, because wait time must be checked against the distribution and the relevant group. Show pooled Q1, Q3, IQR, lower/upper fences, then repeat the relevant grouped check.',
  O3: 'Confirm the lane: Non-IQR, because this is a unit/hard-rule problem, not mainly a distribution-fence problem. Show plausible length range, unit-conversion test, and negative/impossible-length rule.',
  O4: 'Confirm the lane: Non-IQR, because this is a plausible decimal/digit error and impact problem. Show salary range/mean impact, the divide-by-10 hypothesis, and why payroll proof is still needed.',
  O5: 'Confirm the lane: Non-IQR, because this is a rate-denominator problem. Compute conversion rates from clicks/conversions, compare denominators, and state why the rate is unstable or reliable.',
  O6: 'Confirm the lane: Non-IQR, because this is a sentinel/plausibility problem. Show min/range or plausibility check, identify whether the value matches a sentinel/error code, and separate real readings from fake codes.',
  O7: 'Confirm the lane: Non-IQR, because this is a business-signal/impact problem. Compare mean/median or segment revenue, identify whether the high value is a real business signal, and show with/without impact if useful.',
  O8: 'Confirm the lane: Non-IQR, because this is a hard-rule/geocoding problem. Show the hard-rule/range check for distance, explain why zero distance is suspicious, and identify the source check needed.',
};

const PRACTICE_TABLES = [
  {
    id: 'O1',
    title: 'Practice Table O1',
    theme: 'Retail order value',
    tables: [{
      columns: ['order_id', 'customer_id', 'amount'],
      rows: [
        ['R001', 'C101', '42'],
        ['R002', 'C102', '55'],
        ['R003', 'C103', '49'],
        ['R004', 'C104', '5200'],
      ],
    }],
  },
  {
    id: 'O2',
    title: 'Practice Table O2',
    theme: 'Hospital wait time',
    tables: [{
      columns: ['patient_id', 'triage_level', 'wait_minutes', 'case_note'],
      rows: [
        ['P001', 'standard', '38', 'walk-in'],
        ['P002', 'standard', '45', 'walk-in'],
        ['P003', 'urgent', '22', 'fast-track'],
        ['P004', 'transfer', '480', 'delayed ambulance transfer'],
      ],
    }],
  },
  {
    id: 'O3',
    title: 'Practice Table O3',
    theme: 'Warehouse package size',
    tables: [{
      columns: ['package_id', 'product', 'length_cm'],
      rows: [
        ['PK01', 'cable', '18'],
        ['PK02', 'adapter', '12'],
        ['PK03', 'keyboard', '44'],
        ['PK04', 'desk', '1.8'],
      ],
    }],
  },
  {
    id: 'O4',
    title: 'Practice Table O4',
    theme: 'Payroll salary',
    tables: [{
      columns: ['employee_id', 'department', 'salary'],
      rows: [
        ['E001', 'Support', '32000'],
        ['E002', 'Operations', '36500'],
        ['E003', 'Sales', '41000'],
        ['E004', 'Finance', '420000'],
      ],
    }],
  },
  {
    id: 'O5',
    title: 'Practice Table O5',
    theme: 'Marketing campaign conversion',
    tables: [{
      columns: ['campaign_id', 'channel', 'clicks', 'conversions'],
      rows: [
        ['M01', 'search', '900', '45'],
        ['M02', 'email', '1200', '66'],
        ['M03', 'social', '850', '39'],
        ['M04', 'partner', '12', '9'],
      ],
    }],
  },
  {
    id: 'O6',
    title: 'Practice Table O6',
    theme: 'Sensor temperature stream',
    tables: [{
      columns: ['sensor_id', 'reading_time', 'temperature_c'],
      rows: [
        ['S01', '2026-07-01 09:00', '21.4'],
        ['S01', '2026-07-01 10:00', '21.9'],
        ['S01', '2026-07-01 11:00', '-99'],
        ['S01', '2026-07-01 12:00', '22.1'],
      ],
    }],
  },
  {
    id: 'O7',
    title: 'Practice Table O7',
    theme: 'VIP customer revenue',
    tables: [{
      columns: ['customer_id', 'segment', 'monthly_revenue'],
      rows: [
        ['C201', 'standard', '120'],
        ['C202', 'standard', '85'],
        ['C203', 'standard', '140'],
        ['C204', 'enterprise', '12000'],
      ],
    }],
  },
  {
    id: 'O8',
    title: 'Practice Table O8',
    theme: 'Delivery distance',
    tables: [{
      columns: ['delivery_id', 'postcode_area', 'distance_km'],
      rows: [
        ['D001', 'M1', '4.2'],
        ['D002', 'M4', '5.1'],
        ['D003', 'M8', '3.7'],
        ['D004', 'M2', '0'],
      ],
    }],
  },
];

const RUBRIC_TERMS = {
  O1: {
    bucket: ['statistical outlier', 'extreme value'],
    stat: ['z-score', 'iqr', 'boxplot', 'distribution'],
    evidence: ['5200', 'r004'],
    business: ['bulk', 'wholesale', 'fraud', 'order'],
    treatment: ['investigate', 'flag', 'with and without'],
    verify: ['source', 'invoice', 'row count'],
    caution: ['do not delete blindly', 'mean'],
  },
  O2: {
    bucket: ['legitimate extreme', 'real extreme'],
    stat: ['mean', 'median', 'iqr'],
    evidence: ['480', 'p004', 'transfer'],
    business: ['delayed ambulance', 'transfer', 'case note'],
    treatment: ['keep', 'segment', 'report with and without'],
    verify: ['case note', 'service log'],
    caution: ['do not remove', 'median'],
  },
  O3: {
    bucket: ['unit mismatch'],
    stat: ['range', 'min', 'max'],
    evidence: ['1.8', 'desk', 'cm'],
    business: ['metres', 'm', 'unit'],
    treatment: ['convert', 'standardise', 'fix unit'],
    verify: ['cm', 'unit check'],
    caution: ['do not cap', 'not tiny desk'],
  },
  O4: {
    bucket: ['decimal error', 'digit error'],
    stat: ['range', 'z-score', 'iqr'],
    evidence: ['420000', 'e004'],
    business: ['salary band', 'finance', 'payroll'],
    treatment: ['verify source', 'correct', 'quarantine'],
    verify: ['payroll', 'contract'],
    caution: ['do not winsorize', 'personal data'],
  },
  O5: {
    bucket: ['denominator outlier', 'small denominator'],
    stat: ['rate', 'denominator', 'conversion'],
    evidence: ['12', '9', '75%', 'm04'],
    business: ['small sample', 'partner'],
    treatment: ['show counts', 'suppress rate', 'confidence'],
    verify: ['clicks', 'minimum volume'],
    caution: ['do not rank by rate only', 'misleading'],
  },
  O6: {
    bucket: ['sentinel value', 'placeholder'],
    stat: ['min', 'range'],
    evidence: ['-99', 's01'],
    business: ['sensor error', 'missing code'],
    treatment: ['recode null', 'flag', 'fix sensor'],
    verify: ['valid range', 'sensor log'],
    caution: ['do not average -99', 'temperature'],
  },
  O7: {
    bucket: ['important outlier', 'business signal'],
    stat: ['skew', 'median', 'mean'],
    evidence: ['12000', 'enterprise', 'c204'],
    business: ['vip', 'enterprise', 'key account'],
    treatment: ['keep', 'segment', 'separate cohort'],
    verify: ['contract', 'invoice'],
    caution: ['do not delete signal', 'segment'],
  },
  O8: {
    bucket: ['impossible value', 'data error'],
    stat: ['range', 'minimum', 'zero'],
    evidence: ['0', 'd004'],
    business: ['delivery distance', 'postcode'],
    treatment: ['quarantine', 'fix geocode', 'exclude until corrected'],
    verify: ['postcode', 'route', 'geocode'],
    caution: ['do not treat as free delivery', 'do not average'],
  },
};

export const OUTLIER_ANSWER_GUIDE = {
  O1: {
    outlier_bucket: 'Statistical extreme value; needs investigation before treatment.',
    stat_rule: 'Use z-score/IQR/boxplot to flag R004 as unusually high against the order distribution.',
    evidence: 'R004 has amount 5200 while the other orders are around 42-55.',
    business_check: 'Check whether it is a bulk/wholesale order, fraud, refund issue, or data entry problem.',
    treatment: 'Flag and investigate; report metrics with and without the row until the source is confirmed.',
    verify: 'Verify against invoice/source order and confirm row counts and totals after any correction.',
    caution: 'Do not delete R004 just because it is large; it may be real revenue.',
    learner_sentence: 'R004 is a high-value outlier; investigate the business reason and report with/without it before changing the data.',
  },
  O2: {
    outlier_bucket: 'Legitimate extreme value.',
    stat_rule: 'It will pull the mean wait time up; compare mean and median/IQR.',
    evidence: 'P004 waited 480 minutes and has a delayed ambulance transfer note.',
    business_check: 'The case note explains the extreme value, so it may be real service performance evidence.',
    treatment: 'Keep the row, segment transfer cases, and report wait time with and without transfers.',
    verify: 'Confirm the delayed transfer in operational logs or patient movement records.',
    caution: 'Do not remove the row automatically; use median/segmented reporting where appropriate.',
    learner_sentence: 'P004 is a real transfer delay, so keep it and separate transfer analysis from normal walk-in waits.',
  },
  O3: {
    outlier_bucket: 'Unit mismatch outlier.',
    stat_rule: 'Range/min/max checks flag 1.8 cm as impossible for a desk compared with other package lengths.',
    evidence: 'PK04 is a desk with length_cm = 1.8, likely recorded in metres.',
    business_check: 'Check unit capture because desk dimensions may have been entered as metres instead of centimetres.',
    treatment: 'Standardise the unit, likely converting 1.8 m to 180 cm after source confirmation.',
    verify: 'Confirm product dimension unit rules and rerun range checks after conversion.',
    caution: 'Do not cap or delete it as a tiny desk; fix the unit issue.',
    learner_sentence: 'PK04 is likely a unit mismatch; confirm metres vs centimetres and standardise before analysis.',
  },
  O4: {
    outlier_bucket: 'Decimal or digit placement error.',
    stat_rule: 'Range/IQR/z-score checks flag 420000 as far above the salary band.',
    evidence: 'E004 salary is 420000 while comparable salaries are 32000-41000.',
    business_check: 'Check payroll source, role level, and whether an extra zero or annual/monthly confusion occurred.',
    treatment: 'Quarantine or correct only after payroll confirmation; document the correction.',
    verify: 'Verify against payroll/contract and rerun salary distribution checks.',
    caution: 'Do not winsorize payroll casually; salary data is sensitive and needs source proof.',
    learner_sentence: 'E004 looks like a decimal/digit error; verify with payroll before correcting or excluding it.',
  },
  O5: {
    outlier_bucket: 'Rate outlier caused by small denominator.',
    stat_rule: 'Conversion rate is extreme because 9/12 = 75% on a tiny click count.',
    evidence: 'M04 has 12 clicks and 9 conversions, unlike larger campaigns with hundreds of clicks.',
    business_check: 'Check partner campaign volume and whether the small base makes the rate unstable.',
    treatment: 'Show numerator and denominator, apply a minimum-volume rule, or use confidence intervals.',
    verify: 'Confirm click/conversion counts and rerun ranking with a minimum sample threshold.',
    caution: 'Do not rank M04 as best campaign by rate alone.',
    learner_sentence: 'M04 has a high rate from a tiny denominator; show counts and apply a volume threshold before ranking.',
  },
  O6: {
    outlier_bucket: 'Sentinel/placeholder value.',
    stat_rule: 'Min/range check flags -99 as impossible for normal temperature.',
    evidence: 'S01 has one temperature reading of -99 between normal 21-22C readings.',
    business_check: 'Check whether -99 means missing/error code from the sensor system.',
    treatment: 'Recode -99 to null, add an error flag, and investigate sensor ingestion.',
    verify: 'Confirm valid temperature range and source codebook, then rerun averages.',
    caution: 'Do not average -99 as a real temperature.',
    learner_sentence: '-99 is a sentinel reading; recode it to null, flag it, and verify the sensor codebook.',
  },
  O7: {
    outlier_bucket: 'Important business outlier.',
    stat_rule: 'Revenue is highly skewed; mean will be pulled up compared with median.',
    evidence: 'C204 enterprise revenue is 12000 while standard customers are below 150.',
    business_check: 'Enterprise/VIP customers may be the important signal rather than a data defect.',
    treatment: 'Keep the row, segment enterprise customers, and report standard vs enterprise separately.',
    verify: 'Confirm contract/invoice and segment assignment.',
    caution: 'Do not delete high-value customers as outliers; they may drive the business story.',
    learner_sentence: 'C204 is a real enterprise revenue signal; keep it and segment it instead of deleting it.',
  },
  O8: {
    outlier_bucket: 'Impossible or invalid value.',
    stat_rule: 'Minimum/range rule flags zero distance as invalid for a delivery route.',
    evidence: 'D004 has distance_km = 0 while other deliveries are 3.7-5.1 km.',
    business_check: 'Check postcode/geocoding because a real delivery should normally have a positive distance.',
    treatment: 'Quarantine the row or exclude from distance metrics until geocoding is fixed.',
    verify: 'Verify route/postcode geocode and rerun distance statistics after correction.',
    caution: 'Do not average zero distance or treat it as free/no-travel delivery without proof.',
    learner_sentence: 'D004 has an impossible zero distance; fix geocoding or exclude it until verified.',
  },
};

const OUTLIER_CRITICAL_TERMS = {
  O1: { evidence: ['R004', '5200'], learner_sentence: ['R004'] },
  O2: { evidence: ['P004', '480'], business_check: ['transfer'], learner_sentence: ['P004'] },
  O3: { evidence: ['PK04', '1.8'], treatment: ['180'], learner_sentence: ['unit'] },
  O4: { evidence: ['E004', '420000'], business_check: ['payroll'], learner_sentence: ['E004'] },
  O5: { evidence: ['M04', '12', '9'], stat_rule: ['denominator'], learner_sentence: ['denominator'] },
  O6: { evidence: ['-99'], treatment: ['null'], learner_sentence: ['-99'] },
  O7: { evidence: ['C204', '12000'], business_check: ['enterprise'], learner_sentence: ['C204'] },
  O8: { evidence: ['D004', '0'], treatment: ['geocode'], learner_sentence: ['D004'] },
};

const emptyResponses = () =>
  PRACTICE_TABLES.reduce((acc, table) => {
    acc[table.id] = LENSES.reduce((lensAcc, [key]) => ({ ...lensAcc, [key]: '' }), { approach_working: '' });
    return acc;
  }, {});

const normalise = (value) => String(value || '').toLowerCase();
const hasAny = (value, terms = []) => {
  const text = normalise(value);
  return terms.some((term) => text.includes(String(term).toLowerCase()));
};

const lensScore = (value, terms, fallbackMinLength = 16) => {
  const text = normalise(value).trim();
  if (!text) return 0;
  if (hasAny(text, terms)) return 1;
  return text.length >= fallbackMinLength ? 0.5 : 0;
};

const hasCriticalTerm = (value, term) => {
  const text = String(value || '').toLowerCase();
  const token = String(term || '').toLowerCase();
  if (!token) return true;
  return new RegExp(`(^|[^a-z0-9])${token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([^a-z0-9]|$)`).test(text);
};

const applyCriticalTermCap = (tableId, key, value, score) => {
  const criticalTerms = OUTLIER_CRITICAL_TERMS[tableId]?.[key] || [];
  if (!criticalTerms.length || score <= 0) return { score, reason: '' };
  const missingTerms = criticalTerms.filter((term) => !hasCriticalTerm(value, term));
  if (!missingTerms.length) return { score, reason: '' };
  return { score: Math.min(score, 0.5), reason: `Check exact scenario value: ${missingTerms.join(', ')}.` };
};

const countCompletedTables = (responses) =>
  PRACTICE_TABLES.reduce((count, table) => {
    const row = responses[table.id] || {};
    return count + (String(row.approach_working || '').trim() && LENSES.every(([key]) => String(row[key] || '').trim()) ? 1 : 0);
  }, 0);

const missingRequiredFields = (responses) =>
  PRACTICE_TABLES.map((table) => {
    const row = responses[table.id] || {};
    const missing = [];
    if (!String(row.approach_working || '').trim()) missing.push('Approach / working');
    LENSES.forEach(([key, label]) => {
      if (!String(row[key] || '').trim()) missing.push(label);
    });
    return { table, missing };
  }).filter(({ missing }) => missing.length);

const formatMissingFields = (missingTables = []) =>
  missingTables.slice(0, 4).map(({ table, missing }) => `${table.title}: ${missing.join(', ')}`).join(' | ');

const gradeTable = (tableId, answer = {}) => {
  const rubric = RUBRIC_TERMS[tableId];
  if (!rubric) return null;
  const checks = [
    ['stat_rule', 'Detection rule', lensScore(answer.stat_rule, rubric.stat), answer.stat_rule],
    ['evidence', 'Evidence', lensScore(answer.evidence, rubric.evidence), answer.evidence],
    ['business_check', 'Business check', lensScore(answer.business_check, rubric.business), answer.business_check],
    ['outlier_bucket', 'Root cause', lensScore(answer.outlier_bucket, rubric.bucket, 8), answer.outlier_bucket],
    ['treatment', 'Treatment', lensScore(answer.treatment, rubric.treatment), answer.treatment],
    ['caution', 'Caution', lensScore(answer.caution, rubric.caution), answer.caution],
    ['verify', 'Verify', lensScore(answer.verify, rubric.verify), answer.verify],
    ['learner_sentence', 'Learner sentence', lensScore(answer.learner_sentence, [...rubric.bucket, ...rubric.treatment], 40), answer.learner_sentence],
  ].map(([key, label, score, value]) => {
    const capped = applyCriticalTermCap(tableId, key, value, score);
    return [key, label, capped.score, capped.reason];
  });
  const score = checks.reduce((sum, [, , value]) => sum + value, 0);
  const max = checks.length;
  const missing = checks.filter(([, , value]) => value < 1).map(([, label]) => label);
  const strengths = checks.filter(([, , value]) => value >= 1).map(([, label]) => label);
  const lensResults = checks.reduce((acc, [key, label, value, reason]) => ({
    ...acc,
    [key]: {
      label,
      score: value,
      status: value >= 1 ? 'correct' : value > 0 ? 'partial' : 'missed',
      expected: OUTLIER_ANSWER_GUIDE[tableId]?.[key] || '',
      reason,
    },
  }), {});
  return {
    score,
    max,
    percent: Math.round((score / max) * 100),
    strengths,
    missing,
    lensResults,
    feedback: missing.length ? `Review: ${missing.slice(0, 3).join(', ')}${missing.length > 3 ? '...' : ''}.` : 'Strong outlier diagnosis.',
  };
};

const semanticGradeTable = async (table, answer = {}) => {
  const fallback = gradeTable(table.id, answer);
  const lenses = LENSES.map(([key, label]) => ({
    key,
    label,
    user_answer: answer[key] || '',
    expected_answer: OUTLIER_ANSWER_GUIDE[table.id]?.[key] || '',
    critical_terms: OUTLIER_CRITICAL_TERMS[table.id]?.[key] || [],
  }));
  try {
    const result = await analyzeEvidence({
      content: [
        'Activity: DCC outliers take-home semantic grading',
        `Table: ${table.id}`,
        `Theme: ${table.theme}`,
        `Scenario rows: ${JSON.stringify(table.tables)}`,
        `Learner answers: ${JSON.stringify(answer)}`,
        `Expected answers: ${JSON.stringify(OUTLIER_ANSWER_GUIDE[table.id] || {})}`,
      ].join('\n'),
      analysisType: 'semantic-rubric-grade',
      context: {
        activity_scope: 'daily_concept_clinic_take_home',
        rubric_key: 'data_quality_outliers_accuracy',
        table_id: table.id,
        theme: table.theme,
        lenses,
      },
    });
    if (!result?.lensResults) return { ...fallback, mode: 'rules' };
    return {
      ...fallback,
      ...result,
      score: Number(result.score ?? fallback.score),
      max: Number(result.max ?? fallback.max),
      percent: Number(result.percent ?? fallback.percent),
      strengths: result.strengths || fallback.strengths,
      missing: result.missing || fallback.missing,
      lensResults: result.lensResults,
      feedback: result.feedback || fallback.feedback,
      mode: result.mode || 'semantic',
    };
  } catch (error) {
    return { ...fallback, mode: 'rules', llm_error: error.message || 'Semantic grader unavailable.' };
  }
};

const semanticGradeAllTables = async (responses) => {
  const entries = await Promise.all(PRACTICE_TABLES.map(async (table) => [table.id, await semanticGradeTable(table, responses[table.id] || {})]));
  return entries.reduce((acc, [tableId, grade]) => ({ ...acc, [tableId]: grade }), {});
};

const gradeSummary = (grades = {}) => {
  const entries = Object.values(grades).filter(Boolean);
  if (!entries.length) return null;
  const score = entries.reduce((sum, grade) => sum + grade.score, 0);
  const max = entries.reduce((sum, grade) => sum + grade.max, 0);
  const lensCounts = entries.reduce((acc, grade) => {
    (grade.missing || []).forEach((lens) => { acc[lens] = (acc[lens] || 0) + 1; });
    return acc;
  }, {});
  return {
    score,
    max,
    percent: max ? Math.round((score / max) * 100) : 0,
    weaknesses: Object.entries(lensCounts).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([lens]) => lens),
  };
};

function DataTable({ table }) {
  const theme = useTheme();
  return (
    <Box sx={{ my: 1.5, overflowX: 'auto' }}>
      <Box component="table" sx={{
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '0.84rem',
        '& th': { bgcolor: alpha(theme.palette.primary.main, 0.09), fontWeight: 800 },
        '& th, & td': { border: `1px solid ${theme.palette.divider}`, px: 1.25, py: 0.85, textAlign: 'left', verticalAlign: 'top' },
      }}>
        <thead><tr>{table.columns.map((column) => <th key={column}>{column}</th>)}</tr></thead>
        <tbody>{table.rows.map((row, rowIndex) => (
          <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={`${rowIndex}-${cellIndex}`}>{cell}</td>)}</tr>
        ))}</tbody>
      </Box>
    </Box>
  );
}

export default function OutlierPracticeBank() {
  const theme = useTheme();
  const [responses, setResponses] = useState(() => emptyResponses());
  const [grades, setGrades] = useState({});
  const [loadState, setLoadState] = useState('loading');
  const [saveState, setSaveState] = useState('idle');
  const [gradeState, setGradeState] = useState('idle');
  const [message, setMessage] = useState('');
  const loadedRef = useRef(false);
  const saveTimerRef = useRef(null);

  const completedCount = useMemo(() => countCompletedTables(responses), [responses]);
  const missingTables = useMemo(() => missingRequiredFields(responses), [responses]);
  const canGrade = loadState !== 'loading' && !missingTables.length;
  const summary = useMemo(() => gradeSummary(grades), [grades]);

  useEffect(() => {
    let cancelled = false;
    loadRemoteProgress(TRACKER_ID)
      .then((state) => {
        if (cancelled) return;
        setResponses({ ...emptyResponses(), ...(state?.responses || {}) });
        setGrades(state?.grades || {});
      })
      .catch((error) => {
        if (!cancelled && !String(error?.message || '').includes('Unable to load')) setMessage(error.message || 'Could not load saved answers.');
      })
      .finally(() => {
        if (!cancelled) setLoadState('ready');
        loadedRef.current = true;
      });
    return () => {
      cancelled = true;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  const saveResponses = async (nextResponses = responses, mode = 'manual', nextGrades = grades) => {
    setSaveState('saving');
    setMessage('');
    try {
      await saveRemoteProgress(TRACKER_ID, {
        version: 1,
        resource: 'dq-math-04-outliers-accuracy-sample-tables',
        responses: nextResponses,
        grades: nextGrades,
        grade_summary: gradeSummary(nextGrades),
        completed_tables: countCompletedTables(nextResponses),
        updated_at: new Date().toISOString(),
      });
      setSaveState('saved');
      setMessage(mode === 'auto' ? 'Autosaved.' : 'Saved to your outliers practice record.');
    } catch (error) {
      setSaveState('error');
      setMessage(error.message || 'Could not save your answers.');
    }
  };

  const scheduleSave = (nextResponses, nextGrades = grades) => {
    if (!loadedRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaveState('dirty');
    saveTimerRef.current = setTimeout(() => saveResponses(nextResponses, 'auto', nextGrades), 900);
  };

  const updateAnswer = (tableId, key, value) => {
    setResponses((current) => {
      const next = { ...current, [tableId]: { ...(current[tableId] || {}), [key]: value } };
      let nextGrades = grades;
      if (Object.keys(grades).length) {
        nextGrades = { ...grades };
        delete nextGrades[tableId];
        setGrades(nextGrades);
        setMessage(`Table ${tableId} changed. Click Grade answers to run semantic grading again.`);
      }
      scheduleSave(next, nextGrades);
      return next;
    });
  };

  const gradeResponses = async () => {
    if (!canGrade) {
      setMessage(`Finish every table before grading. Still missing: ${formatMissingFields(missingTables)}${missingTables.length > 4 ? ' | ...' : ''}`);
      return;
    }
    setGradeState('grading');
    setMessage('Semantic grading in progress...');
    try {
      const nextGrades = await semanticGradeAllTables(responses);
      setGrades(nextGrades);
      await saveResponses(responses, 'manual', nextGrades);
      const usedAi = Object.values(nextGrades).some((grade) => grade?.mode === 'llm');
      setMessage(usedAi ? 'Semantic grading complete.' : 'Grading complete using the rule fallback. AI semantic grader was unavailable.');
    } finally {
      setGradeState('idle');
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1, bgcolor: alpha(theme.palette.primary.main, 0.035) }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>Learner Practice: Savable Outlier Tables</Typography>
          <Typography variant="body2" color="text.secondary">Show the maths/stat working first, then decide whether each extreme value is error, signal, unit issue, denominator problem, or valid edge case. Your work autosaves to your own practice record.</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
          <Typography variant="caption" color="text.secondary">{completedCount}/{PRACTICE_TABLES.length} complete</Typography>
          {summary && <Chip label={`${summary.percent}%`} color={summary.percent >= 75 ? 'success' : summary.percent >= 50 ? 'warning' : 'default'} size="small" sx={{ fontWeight: 800 }} />}
          <Button variant="outlined" startIcon={<GradeIcon />} onClick={gradeResponses} disabled={saveState === 'saving' || loadState === 'loading' || gradeState === 'grading' || !canGrade} sx={{ textTransform: 'none', fontWeight: 800 }}>
            {gradeState === 'grading' ? 'Grading...' : 'Grade answers'}
          </Button>
          <Button variant="contained" startIcon={saveState === 'saving' ? <CircularProgress color="inherit" size={16} /> : <SaveIcon />} onClick={() => saveResponses()} disabled={saveState === 'saving' || loadState === 'loading'} sx={{ textTransform: 'none', fontWeight: 800 }}>
            {saveState === 'saving' ? 'Saving...' : 'Save now'}
          </Button>
        </Stack>
      </Stack>

      {message && <Alert severity={saveState === 'error' ? 'error' : 'success'} sx={{ mt: 1.5 }}>{message}</Alert>}
      {missingTables.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          Grade answers unlocks after all tables are attempted. Still missing: {formatMissingFields(missingTables)}{missingTables.length > 4 ? ' | ...' : ''}
        </Alert>
      )}
      {summary && (
        <Alert severity={summary.percent >= 70 ? 'success' : 'info'} sx={{ mt: 1.5 }}>
          Score: {summary.score}/{summary.max} ({summary.percent}%). {summary.weaknesses.length ? `Main development areas: ${summary.weaknesses.join(', ')}.` : 'No major weak lens detected.'}
        </Alert>
      )}

      <Alert severity="info" sx={{ mt: 1.5 }}>
        Start each table by naming the detection lane and why: <strong>IQR</strong> for a distribution-fence question, or <strong>Non-IQR</strong> for a rule, unit, rate, group, currency, or impact question. Then show the calculation before filling the Lens.
      </Alert>

      {loadState === 'loading' ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 3 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">Loading your saved answers...</Typography>
        </Stack>
      ) : PRACTICE_TABLES.map((practice) => (
        <Box key={practice.id} sx={{ mt: 3, p: { xs: 1.5, sm: 2 }, border: `1px solid ${theme.palette.divider}`, borderRadius: 1, bgcolor: 'background.paper' }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.75 }}>{practice.title}</Typography>
          <Typography variant="body2" sx={{ mb: 1.25 }}><strong>Theme:</strong> {practice.theme}.</Typography>
          {grades[practice.id] && <Alert severity={grades[practice.id].percent >= 70 ? 'success' : 'info'} sx={{ mb: 1.5 }}>{practice.title} score: {grades[practice.id].score}/{grades[practice.id].max} ({grades[practice.id].percent}%). {grades[practice.id].feedback}</Alert>}
          {practice.tables.map((table, index) => <DataTable key={`${practice.id}-${index}`} table={table} />)}
          <Box sx={{ mt: 2, p: 1.5, border: `1px solid ${alpha(theme.palette.info.main, 0.35)}`, borderRadius: 1, bgcolor: alpha(theme.palette.info.main, 0.045) }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>Approach / working</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {WORKING_PROMPTS[practice.id] || 'Show the maths/stat checks you used before deciding the bucket and treatment.'}
            </Typography>
            <TextField
              value={responses[practice.id]?.approach_working || ''}
              onChange={(event) => updateAnswer(practice.id, 'approach_working', event.target.value)}
              placeholder="Start with: Detection lane: IQR/Non-IQR because... Then show your working: sorted values, IQR fences, rates, conversions, grouped checks, or with/without impact..."
              size="small"
              fullWidth
              multiline
              minRows={4}
            />
          </Box>
          <Box sx={{ overflowX: 'auto', mt: 2 }}>
            <Box component="table" sx={{
              width: '100%',
              borderCollapse: 'collapse',
              '& th': { bgcolor: alpha(theme.palette.primary.main, 0.09), fontWeight: 800 },
              '& th, & td': { border: `1px solid ${theme.palette.divider}`, px: 1.25, py: 0.9, textAlign: 'left', verticalAlign: 'top' },
              '& th:first-of-type, & td:first-of-type': { width: { xs: 150, sm: 210 } },
            }}>
              <thead><tr><th>Lens</th><th>Answer</th></tr></thead>
              <tbody>{LENSES.map(([key, label]) => {
                const lensResult = grades[practice.id]?.lensResults?.[key] || null;
                const missed = lensResult && lensResult.status !== 'correct';
                return (
                  <tr key={key}>
                    <td>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        {lensResult?.status === 'correct' && <CheckCircleIcon color="success" fontSize="small" />}
                        {lensResult?.status === 'partial' && <ErrorOutlineIcon color="warning" fontSize="small" />}
                        {lensResult?.status === 'missed' && <ErrorOutlineIcon color="error" fontSize="small" />}
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{label}</Typography>
                      </Stack>
                    </td>
                    <td>
                      <TextField value={responses[practice.id]?.[key] || ''} onChange={(event) => updateAnswer(practice.id, key, event.target.value)} placeholder={`Type your ${label.toLowerCase()}...`} size="small" fullWidth multiline minRows={key === 'learner_sentence' ? 2 : 1} color={lensResult?.status === 'correct' ? 'success' : lensResult?.status === 'partial' ? 'warning' : 'primary'} focused={Boolean(lensResult)} />
                      {missed && (
                        <Alert severity={lensResult.status === 'partial' ? 'warning' : 'error'} sx={{ mt: 0.75, py: 0 }}>
                          <Typography variant="caption" component="div">Expected direction: {lensResult.expected}</Typography>
                          {lensResult.reason && <Typography variant="caption" component="div">{lensResult.reason}</Typography>}
                        </Alert>
                      )}
                    </td>
                  </tr>
                );
              })}</tbody>
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
}
